import { MenuItem, PresenceBadge } from "@fluentui/react-components";
import { PresenceMenuItemProps } from "./types";
import { MenuView } from "@/components/atoms/menu";
import { mapPresenceToStatus } from "@/common/mappers/presenceMapper";
import { translateActivity } from "@/common/helpers/presenceHelper";
import { useStyle } from "./style";
import { useIntl } from "react-intl";
import { useCallback } from "react";
import { LockClosedFilled, LockOpenFilled } from "@fluentui/react-icons";
import { Activity } from "@/components/organisms/currentUserPresence";

export const PresenceMenuItem = ({
	onClick,
	presenceItem,
	agentState,
	hideLock,
	hideReasons,
}: PresenceMenuItemProps) => {
	const style = useStyle();
	const intl = useIntl();

	const presenceMenuItem = useCallback(
		(availability: string, activity: string, key?: string) => {
			const activityTranslation = translateActivity(activity, intl);

			return (
				<MenuItem
					key={key}
					title={activityTranslation}
					icon={
						<PresenceBadge
							size="small"
							status={mapPresenceToStatus(availability)}
						/>
					}
					className={style.menuItem}
					onClick={(event: React.MouseEvent<HTMLElement>) => {
						event.stopPropagation();
						onClick(availability, activity);
					}}
				>
					{activityTranslation}
				</MenuItem>
			);
		},
		[onClick, intl, style.menuItem]
	);

	const presenceLockMenuItem = useCallback(
		(availability: string) => {
			const isLocked = agentState?.activity === Activity.AvailableLocked;
			const label = isLocked
				? intl.formatMessage({ id: "PresenceActivity.Unlock" })
				: intl.formatMessage({ id: "PresenceActivity.Lock" });

			return (
				<MenuItem
					key={agentState?.id}
					title={label}
					icon={isLocked ? <LockOpenFilled /> : <LockClosedFilled />}
					className={style.menuItem}
					onClick={() =>
						onClick(
							availability,
							isLocked ? Activity.Available : Activity.AvailableLocked
						)
					}
				>
					{label}
				</MenuItem>
			);
		},
		[onClick, intl, style.menuItem, agentState]
	);

	if (!hideReasons && presenceItem.reasons?.length) {
		return (
			<MenuView
				trigger={presenceMenuItem(
					presenceItem.availability,
					presenceItem.activity
				)}
				content={presenceItem.reasons.map((reasonItem) =>
					presenceMenuItem(
						presenceItem.availability,
						reasonItem.name,
						reasonItem.name
					)
				)}
				positioning={{ position: "before" }}
			/>
		);
	}

	if (
		!hideLock &&
		(presenceItem.activity === "Available" ||
			presenceItem.activity === "AvailableLocked")
	) {
		return (
			<MenuView
				trigger={presenceMenuItem(
					presenceItem.availability,
					presenceItem.activity
				)}
				content={[presenceLockMenuItem(presenceItem.availability)]}
				positioning={{ position: "before" }}
			/>
		);
	}

	return presenceMenuItem(presenceItem.availability, presenceItem.activity);
};
